/**
 * Unified Contract Hook
 * Provides consistent contract data access for all user types (buyer, seller, agent)
 */

import {
  useGetContractByAgentQuery,
  useGetContractBySellerQuery,
  useGetContractQuery,
} from '@/apis/extended/contractApi';
import { Contract } from '@/types/ContractType';

/**
 * Unified contract hook that all user types can use
 * This ensures all parties see identical contract data
 *
 * For now, we'll use the main getContract endpoint for all users
 * to ensure consistent data structure
 */
export const useUnifiedContract = (contractId: string) => {
  return useGetContractQuery(contractId, {
    skip: !contractId,
  });
};

/**
 * Hook specifically for seller users
 * Uses the seller-specific endpoint but normalizes the data
 */
export const useUnifiedContractForSeller = (contractId: string) => {
  const sellerQuery = useGetContractBySellerQuery(contractId, {
    skip: !contractId,
  });

  // For now, fall back to the main contract query to ensure consistency
  const mainQuery = useGetContractQuery(contractId, {
    skip: !contractId,
  });

  // Use the main query data if available, otherwise use seller query
  return {
    ...mainQuery,
    data: mainQuery.data || sellerQuery.data,
    isLoading: mainQuery.isLoading || sellerQuery.isLoading,
    error: mainQuery.error || sellerQuery.error,
  };
};

/**
 * Hook specifically for agent users
 * Uses the agent-specific endpoint but normalizes the data
 */
export const useUnifiedContractForAgent = (uid: string) => {
  const agentQuery = useGetContractByAgentQuery(uid, {
    skip: !uid,
  });

  // For now, fall back to the main contract query to ensure consistency
  const mainQuery = useGetContractQuery(uid, {
    skip: !uid,
  });

  // Use the main query data if available, otherwise use agent query
  return {
    ...mainQuery,
    data: mainQuery.data || agentQuery.data,
    isLoading: mainQuery.isLoading || agentQuery.isLoading,
    error: mainQuery.error || agentQuery.error,
  };
};

/**
 * Hook for getting contract with negotiation data
 * Includes contract data plus negotiation status and clauses
 */
export const useUnifiedContractWithNegotiation = (contractId: string) => {
  const contractQuery = useUnifiedContract(contractId);

  return {
    ...contractQuery,
    // Add any additional negotiation-specific data here
    negotiationStatus: contractQuery.data ? 'active' : 'inactive',
    canNegotiate: contractQuery.data
      ? !isContractRatified(contractQuery.data)
      : false,
  };
};

/**
 * Check if contract is ratified (signed by all parties)
 */
function isContractRatified(contract: Contract): boolean {
  if (!contract) return false;

  const buyerSigned = contract.buyerSign?.isStored === true;
  const agentSigned = contract.agentSign?.isStored === true;
  const sellerSigned = contract.sellerSign?.isStored === true;

  return buyerSigned && agentSigned && sellerSigned;
}
